/**
 * CL-DESIGN-BOARDS-DB-3 — Signed read URL cache for design board project files.
 */

import { getProjectDesignBoardFileReadUrl } from "../services/projectDesignBoardFileUploadCallable";

function safeStr(value) {
  return String(value ?? "").trim();
}

let cacheAuthUid = "";

function cacheKey(projectId, fileId, objectKind, pathFingerprint = "") {
  return `${safeStr(cacheAuthUid)}|${safeStr(projectId)}|${safeStr(fileId)}|${safeStr(objectKind) || "original"}|${safeStr(pathFingerprint)}`;
}

const readUrlCache = new Map();

/** Clears cached signed URLs when the signed-in user changes or signs out. */
export function syncDesignBoardFileReadUrlCacheUser(authUid = "") {
  const nextUid = safeStr(authUid);
  if (nextUid === cacheAuthUid) return;
  cacheAuthUid = nextUid;
  readUrlCache.clear();
}

export function getCachedDesignBoardFileReadUrl(projectId, fileId, objectKind = "original", pathFingerprint = "") {
  return readUrlCache.get(cacheKey(projectId, fileId, objectKind, pathFingerprint)) || "";
}

export function setCachedDesignBoardFileReadUrl(projectId, fileId, objectKind, readUrl, pathFingerprint = "") {
  const url = safeStr(readUrl);
  if (!url) return;
  readUrlCache.set(cacheKey(projectId, fileId, objectKind, pathFingerprint), url);
}

export function invalidateDesignBoardFileReadUrl(projectId, fileId, objectKind = "original", pathFingerprint = "") {
  readUrlCache.delete(cacheKey(projectId, fileId, objectKind, pathFingerprint));
}

export function clearDesignBoardFileReadUrlCache() {
  readUrlCache.clear();
  cacheAuthUid = "";
}

export async function fetchDesignBoardFileReadUrl(
  { projectId = "", fileId = "", objectKind = "original", pathFingerprint = "" } = {},
  { getReadUrl = getProjectDesignBoardFileReadUrl } = {}
) {
  const pid = safeStr(projectId);
  const fid = safeStr(fileId);
  const kind = safeStr(objectKind) === "thumbnail" ? "thumbnail" : "original";
  const fingerprint = safeStr(pathFingerprint);
  if (!pid || !fid) {
    return { ok: false, errorCode: "invalid_args", error: "Invalid file reference." };
  }
  const cached = getCachedDesignBoardFileReadUrl(pid, fid, kind, fingerprint);
  if (cached) {
    return { ok: true, readUrl: cached, fromCache: true, objectKind: kind };
  }
  let result;
  try {
    result = await getReadUrl({ projectId: pid, fileId: fid, objectKind: kind });
  } catch (error) {
    invalidateDesignBoardFileReadUrl(pid, fid, kind, fingerprint);
    const code = safeStr(error?.cause?.code || error?.code);
    const notFound = code === "functions/not-found" || code === "functions/failed-precondition";
    return {
      ok: false,
      errorCode: notFound ? "object_not_found" : "read_url_failed",
      error: safeStr(error?.message) || "Could not load this file.",
    };
  }
  if (!result?.ok || !result?.readUrl) {
    invalidateDesignBoardFileReadUrl(pid, fid, kind, fingerprint);
    return {
      ok: false,
      errorCode: "read_url_failed",
      error: safeStr(result?.error) || "Could not load this file.",
    };
  }
  setCachedDesignBoardFileReadUrl(pid, fid, kind, result.readUrl, fingerprint);
  return { ok: true, readUrl: result.readUrl, fromCache: false, objectKind: kind, expiresAt: result.expiresAt };
}
